
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api`;

const TeacherLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [schoolDetails, setSchoolDetails] = useState(null);

  const navigate = useNavigate();

  // -------------------- FETCH SCHOOL DETAILS --------------------
  useEffect(() => {
    axios
      .get(`${API_BASE}/school/getSchool`)
      .then((res) => {
        if (res.data) {
          setSchoolDetails(
            Array.isArray(res.data) ? res.data[0] : res.data
          );
        }
      })
      .catch(() => {});
  }, []);

  // -------------------- FETCH SESSIONS --------------------
  useEffect(() => {
    axios
      .get(`${API_BASE}/sessions/allSessions`)
      .then((res) => setSessions(res.data))
      .catch(() => setError("Failed to load academic sessions"));
  }, []);

  // -------------------- LOGIN HANDLER --------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    // Teacher must enter the existing credentials
    // and select an academic session.
    if (!username.trim() || !password || !sessionId) {
      setError("Please fill all fields");
      return;
    }

    setLoading(true);

    try {
      // IMPORTANT:
      // Send the username and password exactly as entered.
      // No username/password is generated or changed here.
      const response = await axios.post(`${API_BASE}/auth/login`, {
        username: username.trim(),
        password,
        sessionId,
      });

      // Handle forced password-change response separately.
      // This does NOT change the password from TeacherLogin.
      if (response.data.requirePasswordChange) {
        localStorage.setItem("pendingPasswordUserId", response.data.userId);

        setError(
          response.data.message || "Password reset required"
        );

        return;
      }

      // Save authentication details
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("role", "teacher");
      localStorage.setItem(
        "teacher",
        JSON.stringify(response.data.teacher)
      );
      localStorage.setItem(
        "currentSessionId",
        response.data.sessionId || sessionId
      );

      // Notify other components about login/storage change
      window.dispatchEvent(new Event("storage"));

      // Go to dashboard
      navigate("/dashboard", { replace: true });

    } catch (err) {
      console.error("Teacher login error:", err);

      setError(
        err.response?.data?.message ||
          "Invalid username or password"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen">

      {/* LEFT SECTION */}
      <div className="hidden md:flex w-1/2 bg-gradient-to-br from-blue-600 to-blue-900 justify-center items-center">
        <div className="text-center text-white p-8">

          <div className="flex justify-center mb-6">
            <img
              src="/assets/gurukulerpogin.png"
              alt="Gurukul Logo"
              className="h-[20vh] w-auto object-contain"
            />
          </div>

          <h1 className="text-5xl font-bold mb-4">
            Welcome to Gurukul
          </h1>

          <p className="text-lg">
            Your journey to knowledge and excellence begins here.
          </p>

        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="flex w-full md:w-1/2 justify-center items-center bg-white">
        <div className="w-full max-w-md p-8">

          {/* SCHOOL LOGO */}
          {schoolDetails?.logo && (
            <div className="flex justify-center mb-6">
              <img
                src={schoolDetails.logo}
                alt="School Logo"
                className="h-28 w-auto object-contain"
              />
            </div>
          )}

          <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">
            Teacher Login
          </h2>

          {/* ERROR */}
          {error && (
            <p className="text-red-500 text-center mb-4">
              {error}
            </p>
          )}

          <form onSubmit={handleLogin} className="space-y-4">

            {/* USERNAME */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Username
              </label>

              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="Enter username"
                autoComplete="username"
              />
            </div>

            {/* PASSWORD */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>

            {/* SESSION SELECT */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Academic Session
              </label>

              <select
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg"
              >
                <option value="">
                  Select Academic Session
                </option>

                {sessions.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.sessionName}
                  </option>
                ))}
              </select>
            </div>

            {/* SUBMIT */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Log In"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default TeacherLogin;
